import { formatMoney, minorUnitsFor } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { type FormEvent, useEffect, useState } from "react";
import {
  applyCreditNote,
  applyPrepayment,
  type CreditNote,
  type Invoice,
  listCreditNotes,
  listPrepayments,
  type Prepayment,
  previewReminders,
  recordPayment,
} from "@/lib/api";
import { Link } from "@tanstack/react-router";
import { ActionMenu, type ActionMenuItem } from "@/components/ActionMenu";
import { PeriodLockWarning } from "@/components/PeriodLockWarning";
import { minorUnitEntry, toMinorUnits } from "@/lib/money";
import { canCreditInvoice, canRemindInvoice, payableBalanceCents } from "@/lib/worklist";

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function message(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/** Row actions for one invoice. Payment, credit, and prepayment stay on the list. */
export function InvoiceRowActions({
  invoice,
  onChanged,
  onCopyLink,
  onDownloadPdf,
  onPrepaymentApplied,
  onCreateCreditNote,
  copyBusy,
  pdfBusy,
}: {
  invoice: Invoice;
  onChanged: () => void;
  onCopyLink: (invoiceId: string) => void;
  onDownloadPdf: (invoiceId: string) => void;
  onPrepaymentApplied?: (journalEntryId?: string) => void;
  onCreateCreditNote: (invoiceId: string) => void;
  copyBusy: boolean;
  pdfBusy: boolean;
}) {
  const [payOpen, setPayOpen] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const owed = payableBalanceCents(invoice) > 0;
  const openInvoice = owed && invoice.status.toLowerCase() !== "draft";
  const canRecordPayment = openInvoice && Boolean(invoice.crm_contact_id);
  const currency = invoice.currency_code || "USD";

  const items: ActionMenuItem[] = [
    {
      id: "open",
      label: "Open invoice",
      to: "/invoices/$invoiceId",
      params: { invoiceId: invoice.id },
    },
    ...(canRecordPayment
      ? [
          {
            id: "record-payment",
            label: "Record payment",
            onSelect: () => setPayOpen(true),
          },
        ]
      : []),
    ...(canRemindInvoice(invoice)
      ? [
          {
            id: "send-reminder",
            label: "Send reminder",
            onSelect: () => setRemindOpen(true),
          },
        ]
      : []),
    ...(canCreditInvoice(invoice)
      ? [
          {
            id: "create-credit-note",
            label: "Create credit note",
            onSelect: () => onCreateCreditNote(invoice.id),
          },
          {
            id: "apply-credit",
            label: "Apply credit note",
            onSelect: () => setCreditOpen(true),
          },
        ]
      : []),
    ...(openInvoice
      ? [
          {
            id: "apply-prepayment",
            label: "Apply prepayment",
            onSelect: () => setApplyOpen(true),
          },
        ]
      : []),
    {
      id: "download-pdf",
      label: "Download PDF",
      busy: pdfBusy,
      busyLabel: "Saving…",
      onSelect: () => onDownloadPdf(invoice.id),
    },
    {
      id: "copy-link",
      label: "Copy customer link",
      busy: copyBusy,
      busyLabel: "Copying…",
      onSelect: () => onCopyLink(invoice.id),
    },
  ];

  return (
    <>
      <ActionMenu
        label="Actions"
        ariaLabel={`Actions for ${invoice.invoice_number}`}
        items={items}
      />
      {payOpen ? (
        <RecordPaymentDialog
          invoiceId={invoice.id}
          currency={currency}
          onClose={() => setPayOpen(false)}
          onDone={onChanged}
        />
      ) : null}
      {remindOpen ? (
        <ReminderDialog
          invoiceId={invoice.id}
          customerName={invoice.customer_name}
          onClose={() => setRemindOpen(false)}
        />
      ) : null}
      {creditOpen ? (
        <ApplyCreditDialog
          invoice={invoice}
          onClose={() => setCreditOpen(false)}
          onDone={onChanged}
          onCreateCreditNote={onCreateCreditNote}
        />
      ) : null}
      {applyOpen ? (
        <ApplyPrepaymentDialog
          invoice={invoice}
          currency={currency}
          onClose={() => setApplyOpen(false)}
          onDone={onChanged}
          onApplied={onPrepaymentApplied}
        />
      ) : null}
    </>
  );
}

export function RecordPaymentDialog({
  invoiceId,
  currency,
  lockDate,
  onClose,
  onDone,
}: {
  invoiceId: string;
  currency: string;
  lockDate?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank_transfer");
  const [received, setReceived] = useState(todayInput);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const entry = minorUnitEntry(currency);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const amount_minor = toMinorUnits(amount, currency);
      if (amount_minor === null)
        throw new Error("Amount must be a positive number (e.g. 12.50).");
      if (!received) throw new Error("Received date is required.");
      await recordPayment({
        allocations: [{ invoice_id: invoiceId, amount_minor }],
        amount_minor,
        method: method.trim(),
        received_at: new Date(`${received}T00:00:00`).toISOString(),
      });
      onDone();
      onClose();
    } catch (cause) {
      setError(message(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Record a payment"
      description="Posts a customer payment against this invoice."
    >
      <form onSubmit={submit} className="grid gap-3">
        <PeriodLockWarning lockDate={lockDate} chosenDate={received} />
        {error ? (
          <p role="alert" className="text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        <label className="grid gap-1 text-xs font-medium" htmlFor={`pay-amount-${invoiceId}`}>
          Amount ({currency})
          <Input
            id={`pay-amount-${invoiceId}`}
            type="number"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            required
            placeholder={entry.placeholder}
            step={entry.step}
            min="0"
            inputMode="decimal"
            disabled={saving}
          />
        </label>
        <label className="grid gap-1 text-xs font-medium" htmlFor={`pay-method-${invoiceId}`}>
          Method
          <Input
            id={`pay-method-${invoiceId}`}
            value={method}
            onChange={(event) => setMethod(event.target.value)}
            required
            disabled={saving}
          />
        </label>
        <label className="grid gap-1 text-xs font-medium" htmlFor={`pay-date-${invoiceId}`}>
          Received on
          <Input
            id={`pay-date-${invoiceId}`}
            type="date"
            value={received}
            onChange={(event) => setReceived(event.target.value)}
            required
            disabled={saving}
          />
        </label>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? "Recording…" : "Record payment"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function ReminderDialog({
  invoiceId,
  customerName,
  onClose,
}: {
  invoiceId: string;
  customerName: string;
  onClose: () => void;
}) {
  const [text, setText] = useState("Checking the reminder rules…");

  useEffect(() => {
    let cancelled = false;
    previewReminders({
      document_type: "invoice",
      trigger: "after_due",
      trigger_days: 1,
      recipient: "contact_email",
    })
      .then((candidates) => {
        if (cancelled) return;
        const match = (Array.isArray(candidates) ? candidates : []).find(
          (row) => row.document_id === invoiceId,
        );
        setText(
          match
            ? `${match.subject}. Review the queue and confirm before anything is emailed.`
            : `No reminder is due for ${customerName} right now.`,
        );
      })
      .catch((cause: unknown) => {
        if (!cancelled) setText(message(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [customerName, invoiceId]);

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Send a reminder"
      description="Nothing is emailed from here. Mail goes out from Payment reminders."
    >
      <p className="text-sm">{text}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button asChild>
          <Link to="/reminders">Open reminder queue</Link>
        </Button>
      </div>
    </Dialog>
  );
}

export function ApplyCreditDialog({
  invoice,
  onClose,
  onDone,
  onCreateCreditNote,
}: {
  invoice: Invoice;
  onClose: () => void;
  onDone: () => void;
  onCreateCreditNote?: (invoiceId: string) => void;
}) {
  const [rows, setRows] = useState<CreditNote[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [noteId, setNoteId] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const currency = invoice.currency_code || "USD";
  const selected = rows?.find((row) => row.id === noteId);

  useEffect(() => {
    let cancelled = false;
    listCreditNotes()
      .then((list) => {
        if (cancelled) return;
        const available = list.filter(
          (row) =>
            row.related_invoice_id === invoice.id &&
            row.status.toLowerCase() === "sent",
        );
        setRows(available);
        setNoteId(available[0]?.id ?? "");
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(message(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [invoice.id]);

  const confirm = async () => {
    if (!noteId) return;
    setSaving(true);
    setError(null);
    try {
      await applyCreditNote(noteId);
      onDone();
      onClose();
    } catch (cause) {
      setConfirming(false);
      setError(message(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={confirming ? "Apply this credit note?" : "Apply a credit note"}
      description={
        confirming
          ? "This posts the AR adjustment and reduces the invoice balance. It is not a cash refund."
          : "Applies a sent credit note to this invoice. No cash moves."
      }
    >
      {confirming ? (
        <div className="grid gap-3">
          {error ? (
            <p role="alert" className="text-sm text-[var(--danger)]">
              {error}
            </p>
          ) : null}
          <p className="text-sm">
            {selected
              ? `${selected.credit_note_number} — ${formatMoney({ cents: selected.total_minor, currency })}`
              : "The selected credit note"}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setConfirming(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="button" onClick={() => void confirm()} disabled={saving}>
              {saving ? "Applying…" : "Apply credit"}
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!noteId) {
              setError("Choose a credit note.");
              return;
            }
            setError(null);
            setConfirming(true);
          }}
        >
          {error ? (
            <p role="alert" className="text-sm text-[var(--danger)]">
              {error}
            </p>
          ) : null}
          {rows === null && !error ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              Loading credit notes…
            </p>
          ) : rows && rows.length === 0 ? (
            <div className="grid gap-2 text-sm text-[var(--muted-foreground)]">
              <p>No sent credit note for this invoice.</p>
              <p>Create a credit note first, then mark it sent.</p>
              {onCreateCreditNote ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="justify-self-start"
                  onClick={() => {
                    onClose();
                    onCreateCreditNote(invoice.id);
                  }}
                >
                  Create credit note
                </Button>
              ) : null}
            </div>
          ) : (
            <>
              <label className="grid gap-1 text-xs font-medium" htmlFor={`apply-credit-${invoice.id}`}>
                Credit note
                <select
                  id={`apply-credit-${invoice.id}`}
                  className="select"
                  value={noteId}
                  onChange={(event) => setNoteId(event.target.value)}
                >
                  {(rows ?? []).map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.credit_note_number} —{" "}
                      {formatMoney({ cents: row.total_minor, currency })}
                    </option>
                  ))}
                </select>
              </label>
              <Button type="submit">Apply credit</Button>
            </>
          )}
        </form>
      )}
    </Dialog>
  );
}

function prepaymentAmount(row: Prepayment, owed: number, fallbackCurrency: string): string {
  const code = row.currency_code || fallbackCurrency;
  const decimals = minorUnitsFor(code);
  return (Math.min(row.unapplied_minor, owed) / 10 ** decimals).toFixed(decimals);
}

export function ApplyPrepaymentDialog({
  invoice,
  currency,
  onClose,
  onDone,
  onApplied,
}: {
  invoice: Invoice;
  currency: string;
  onClose: () => void;
  onDone: () => void;
  onApplied?: (journalEntryId?: string) => void;
}) {
  const [rows, setRows] = useState<Prepayment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [prepaymentId, setPrepaymentId] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const selected = rows?.find((row) => row.id === prepaymentId);
  const payCurrency = selected?.currency_code || currency;
  const entry = minorUnitEntry(payCurrency);

  useEffect(() => {
    let cancelled = false;
    listPrepayments()
      .then((list) => {
        if (cancelled) return;
        const customer = invoice.customer_name.trim().toLowerCase();
        const available = list.filter(
          (row) =>
            row.unapplied_minor > 0 &&
            row.customer_name.trim().toLowerCase() === customer,
        );
        setRows(available);
        const first = available[0];
        if (first) {
          setPrepaymentId(first.id);
          setAmount(prepaymentAmount(first, payableBalanceCents(invoice), currency));
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(message(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [currency, invoice]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!prepaymentId) {
      setError("Choose a prepayment.");
      return;
    }
    if (toMinorUnits(amount, payCurrency) === null) {
      setError("Amount must be a positive number.");
      return;
    }
    setConfirming(true);
  };

  const confirm = async () => {
    setSaving(true);
    setError(null);
    try {
      const amount_minor = toMinorUnits(amount, payCurrency);
      if (!prepaymentId) throw new Error("Choose a prepayment.");
      if (amount_minor === null)
        throw new Error("Amount must be a positive number.");
      const result = await applyPrepayment(prepaymentId, {
        invoice_id: invoice.id,
        amount_minor,
      });
      onApplied?.(result.journal_entry_id);
      onDone();
      onClose();
    } catch (cause) {
      setConfirming(false);
      setError(message(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={confirming ? "Apply this prepayment?" : "Apply a prepayment"}
      description={
        confirming
          ? "This posts immediately and moves no cash."
          : "Applies this customer's prepayment to the open balance. No new cash is recorded."
      }
    >
      {confirming ? (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirming(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type="button" onClick={() => void confirm()} disabled={saving}>
            {saving ? "Applying…" : "Apply prepayment"}
          </Button>
        </div>
      ) : (
      <form onSubmit={submit} className="grid gap-3">
        {error ? (
          <p role="alert" className="text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        {rows === null && !error ? (
          <p className="text-sm text-[var(--muted-foreground)]">Loading prepayments…</p>
        ) : rows && rows.length === 0 ? (
          <p className="text-sm text-[var(--muted-foreground)]">
            {invoice.customer_name} has no unapplied prepayment.
          </p>
        ) : (
          <>
            <label className="grid gap-1 text-xs font-medium" htmlFor={`apply-prepay-${invoice.id}`}>
              Prepayment
              <select
                id={`apply-prepay-${invoice.id}`}
                className="select"
                value={prepaymentId}
                onChange={(event) => {
                  const nextId = event.target.value;
                  setPrepaymentId(nextId);
                  const row = (rows ?? []).find((item) => item.id === nextId);
                  if (row) {
                    setAmount(
                      prepaymentAmount(row, payableBalanceCents(invoice), currency),
                    );
                  }
                }}
              >
                {(rows ?? []).map((row) => (
                  <option key={row.id} value={row.id}>
                    {formatMoney({
                      cents: row.unapplied_minor,
                      currency: row.currency_code || currency,
                    })}{" "}
                    available
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-xs font-medium" htmlFor={`apply-amount-${invoice.id}`}>
              Amount ({payCurrency})
              <Input
                id={`apply-amount-${invoice.id}`}
                type="number"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                required
                min="0"
                step={entry.step}
                placeholder={entry.placeholder}
                inputMode="decimal"
                disabled={saving}
              />
            </label>
            <Button type="submit" disabled={saving}>
              Apply prepayment
            </Button>
          </>
        )}
      </form>
      )}
    </Dialog>
  );
}
